import {
  computeResolvedTheme,
  getStoredAppearanceMode,
  MODE_TO_THEME,
} from '@/app-shell/app-root/useAppearanceMode';
import { setAppearanceUserProperties } from '@/providers/amplitude-user-properties';
import { usePosthogFlagsReady } from '@/providers/posthog-flags';
import { Theme } from '@heidi/schema/ml-scribe';
import { useTheme } from 'next-themes';
import { useEffect, useRef } from 'react';

/**
 * Applies the stored appearance preference to next-themes, and keeps the
 * Amplitude appearance user properties fresh for sessions where the user never
 * touches the toggle (`setMode` covers the rest).
 *
 * The two effects have deliberately opposite timing — don't merge them:
 *
 *  1. **Theme** — ungated, `[]` deps. It corrects whatever `next-themes` painted
 *     from its own storage key, so earlier is strictly better. The `setTheme` ref
 *     is not staleness protection; it only keeps `setTheme` out of the dep array.
 *     A regression test pins the empty deps and carries the flicker history.
 *  2. **User properties** — gated on `flagsReady`, which here is an "identity has
 *     settled" signal, NOT a feature-flag read. The write is sticky (see
 *     `amplitude-user-properties.ts`), so writing before Amplitude has a user id
 *     pins the properties to the anonymous device for the whole page load. Only
 *     bites on a first-ever session, when there is no persisted identity to
 *     re-attach at init.
 *
 * Do NOT sync the backend `user.theme` field from here: `useMutateUpdateUser`
 * drags in a circular auth/store dependency and its stale query cache caused
 * flicker. The preference is device-local; the backend field is legacy.
 */
export const UserThemeManager = () => {
  const { setTheme } = useTheme();
  const flagsReady = usePosthogFlagsReady();

  const setThemeRef = useRef(setTheme);
  setThemeRef.current = setTheme;

  // Read by the matchMedia listener, which registers once on mount and so
  // cannot close over `flagsReady` directly.
  const flagsReadyRef = useRef(flagsReady);
  flagsReadyRef.current = flagsReady;

  useEffect(() => {
    const storedMode = getStoredAppearanceMode();

    if (storedMode === 'system') {
      setThemeRef.current('system');
      return;
    }

    if (storedMode === 'light' || storedMode === 'dark') {
      setThemeRef.current(MODE_TO_THEME[storedMode]);
      return;
    }

    setThemeRef.current(Theme.newBrand);
  }, []);

  useEffect(() => {
    if (!flagsReady) return;

    // Mode-agnostic on purpose: no parallel switch to drift from the effect above.
    const mode = getStoredAppearanceMode() ?? 'light';
    setAppearanceUserProperties(mode, computeResolvedTheme(mode));
  }, [flagsReady]);

  // Refresh the `resolved_theme` user property when the OS preference flips
  // mid-session for users on `system` mode. Without this, the property goes
  // stale on the cohort with the most flux — defeating the point of the
  // stock metric for system-mode users. No flow event fires; the user did
  // not actively change anything, only the OS did.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => {
      // Identity has not settled yet, and the user-property cache has no
      // production reset — writing now would pin the properties to the
      // anonymous device id and dedupe away the later, correct write.
      // Dropping this flip is safe: the effect above re-reads
      // `computeResolvedTheme` when `flagsReady` flips, so it self-heals.
      if (!flagsReadyRef.current) return;
      if (getStoredAppearanceMode() !== 'system') return;
      setAppearanceUserProperties('system', e.matches ? 'dark' : 'light');
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return null;
};
